import { PropTypes } from "base/react-libs";
import { useIdentity } from "base/identity-resolver";
import { fetchJourneyDetails } from "../utils/api-requests/journey-details";
import { useEffect, useState } from "react";
import { useTasksNotificationContext } from "apps/tasks/hooks/tasks-notification-context.js";
import useJourneyTitles from "apps/journeys/hooks/use-journey-titles";
import JourneyDetail from "../components/detail/journey-detail";
import useJourneyGroups from "apps/journeys/hooks/use-journey-groups";

// THIS IS THE ONLY STATEFUL COMPONENT. EVERYTHING BENEATH IT RECEIVES PROPS.
const today = new Date();
const JourneyDetailController = ({ journeyId }) => {
  const { user } = useIdentity();
  const [journey, setJourney] = useState({});
  const [selectedTask, setSelectedTask] = useState(null); // for right-side panel view
  const [selectedDueDate, setSelectedDueDate] = useState(today.getTime());
  const { getAndSetGlobalTasksDueCount } = useTasksNotificationContext();

  // These two hooks are only necessary because of the Journeys Title List ("the pills at the top of the page")
  // once we get rid of those, we can remove these two hooks.
  const { journeyGroups, setSelectedJourneyGroup, selectedJourneyGroup } = useJourneyGroups();
  const { journeyTitles, updateJourneyTitleNumber } = useJourneyTitles(selectedDueDate, selectedJourneyGroup, false);

  const selectJourneyGroup = (group) => {
    setSelectedJourneyGroup(group);
    setJourney({});
  };

  useEffect(() => {
    const getAndSetJourney = async () => {
      const journeyFromGql = await fetchJourneyDetails(
        Number(journeyId),
        user.id,
        ["IN_PROGRESS", "COMPLETED"],
        selectedDueDate
      );
      setJourney(journeyFromGql);
    };

    getAndSetJourney();
    setSelectedTask(null);
  }, [journeyId, selectedDueDate, user.id]);

  // STATE MANAGEMENT FUNCTIONS

  const handleCompletionToggle = (step, task, completed_at) => {
    task.completed_at = completed_at;
    handleUpdateTask(task, step);
    updateJourneyTitleNumber(step, task, completed_at); // remove when we get rid of the journey pills
    getAndSetGlobalTasksDueCount();
  };

  const handleRemoveTask = (task, step) => {
    const journeyCopy = { ...journey };
    const { steps } = journeyCopy;
    const stepCopy = steps.find((s) => s.id === step.id); // find correct step
    const replaceStepIndex = steps.findIndex((s) => s.id === step.id); // this is the step to update in-place
    const { tasks } = stepCopy;
    const filtered = tasks.filter((t) => !(t.id === task.id));
    stepCopy.tasks = filtered;
    steps[replaceStepIndex] = stepCopy;
    journeyCopy.steps = steps;
    getAndSetGlobalTasksDueCount();
    setJourney(journeyCopy);
  };

  const handleUpdateTask = (updatedTask, step) => {
    const journeyCopy = { ...journey };
    const { steps } = journeyCopy;
    const stepCopy = steps.find((j) => j.id === step.id); // find correct step
    const replaceStepIndex = steps.findIndex((s) => s.id === step.id); // this is the step to update in-place
    const { tasks } = stepCopy;
    const replaceTaskIndex = tasks.findIndex((t) => t.id === updatedTask.id); // this is the task to update in-place
    tasks[replaceTaskIndex] = updatedTask;
    stepCopy.tasks = tasks;
    steps[replaceStepIndex] = stepCopy;
    journeyCopy.steps = steps;
    setJourney(journeyCopy);
    getAndSetGlobalTasksDueCount();
  };

  const handleRemoveContact = (contactIdToRemove) => {
    const journeyCopy = { ...journey };
    const { steps = [] } = journeyCopy;
    const filteredSteps = steps.map((step) => {
      const { tasks = [] } = step;
      const filtered = tasks.filter(({ contact }) => !(contact.id === contactIdToRemove));
      step.tasks = filtered;
      return step;
    });
    journeyCopy.steps = filteredSteps;
    setJourney(journeyCopy);
    getAndSetGlobalTasksDueCount();
    setSelectedTask(null);
  };

  // We pass this function as a prop to the task table
  // it is used by the modals to update the state of the table
  // we flag it as optional, which allows us to use the modals elsewhere
  // without needing to worry about managing the state of a component.
  const optionalStateManagementFunction = (type = "", args = {}) => {
    switch (type) {
      case "removeContact":
        handleRemoveContact(args.contactIdToRemove);
        break;
      case "removeTask":
        handleRemoveTask(args.task, args.step);
        break;
      case "updateTask":
        handleUpdateTask(args.task, args.step);
        break;
      case "completionToggle":
        handleCompletionToggle(args.step, args.task, args.completed_at);
        break;
      default:
        break;
    }
  };

  return (
    <JourneyDetail
      journey={journey}
      journeys={journeyTitles}
      selectedTask={selectedTask}
      journeyGroups={journeyGroups}
      setSelectedTask={setSelectedTask}
      selectedDueDate={selectedDueDate}
      setSelectedDueDate={setSelectedDueDate}
      selectedJourneyGroup={selectedJourneyGroup}
      setSelectedJourneyGroup={selectJourneyGroup}
      optionalStateManagementFunction={optionalStateManagementFunction}
    />
  );
};

JourneyDetailController.propTypes = {
  journeyId: PropTypes.string.isRequired,
};

export default JourneyDetailController;
